import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'

export default function Admin() {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [analyses, setAnalyses] = useState([])
  const [accessRequests, setAccessRequests] = useState([])
  const [pendingCount, setPendingCount] = useState(0)
  const [activeTab, setActiveTab] = useState('users')
  const [requestFilter, setRequestFilter] = useState('all')
  const [tempPassword, setTempPassword] = useState('')
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [chartData, setChartData] = useState({ daily: [], searchesByUser: [], roleDistribution: [] })
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' })
  const [roleFilter, setRoleFilter] = useState('all')
  const [confidenceFilter, setConfidenceFilter] = useState('all')
  const [searchText, setSearchText] = useState('')
  const [dbStatus, setDbStatus] = useState({ connected: false, lastChecked: null })
  const [dataRefreshing, setDataRefreshing] = useState(false)

  useEffect(() => {
    checkAuth()
    checkDatabaseConnection()
  }, [])

  async function checkDatabaseConnection() {
    try {
      const { error } = await supabase.from('profiles').select('count').limit(1).maybeSingle()
      setDbStatus({
        connected: !error,
        lastChecked: new Date().toISOString()
      })
      if (error) {
        console.error('[ADMIN] Database connection check failed:', error)
      }
    } catch (error) {
      console.error('[ADMIN] Database connection check error:', error)
      setDbStatus({ connected: false, lastChecked: new Date().toISOString() })
    }
  }

  async function refreshAllData() {
    setDataRefreshing(true)
    try {
      await Promise.all([
        loadUsers(),
        loadAnalyses(),
        loadAccessRequests()
      ])
      await checkDatabaseConnection()
    } finally {
      setDataRefreshing(false)
    }
  }

  async function checkAuth() {
    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        navigate('/login', { replace: true })
        return
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle()

      if (!profile || (profile.role !== 'admin' && profile.role !== 'premium')) {
        navigate('/dashboard', { replace: true })
        return
      }

      setUser(session.user)
      setUserProfile(profile)
      await loadUsers()
      await loadAnalyses()
      await loadAccessRequests()
      setLoading(false)
    } catch (error) {
      console.error('Auth error:', error)
      navigate('/login', { replace: true })
    }
  }

  async function loadUsers() {
    try {
      console.log('[ADMIN] Loading users...')

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })

      if (profilesError) {
        console.error('[ADMIN] Error loading profiles:', profilesError)
        alert(`Database connection error: ${profilesError.message}. Please refresh the page.`)
        setUsers([])
        return
      }

      console.log(`[ADMIN] Loaded ${profilesData?.length || 0} profiles from database`)

      const { data: { users: authUsers }, error: authError } = await supabase.auth.admin.listUsers()

      if (authError) {
        console.error('[ADMIN] Error loading auth users:', authError)
        console.log('[ADMIN] Showing profiles without email addresses')
        setUsers(profilesData || [])
        return
      }

      console.log(`[ADMIN] Loaded ${authUsers?.length || 0} auth users`)

      const usersWithEmails = (profilesData || []).map(profile => {
        const authUser = authUsers?.find(au => au.id === profile.id)
        return {
          ...profile,
          email: authUser?.email || profile.email || 'N/A'
        }
      })

      console.log(`[ADMIN] Final user list: ${usersWithEmails.length} users`)
      setUsers(usersWithEmails)
    } catch (error) {
      console.error('[ADMIN] Unexpected error loading users:', error)
      alert('Failed to load users. Please check your connection and try again.')
      setUsers([])
    }
  }

  async function loadAnalyses() {
    try {
      console.log('[ADMIN] Loading analyses...')
      const { data, error } = await supabase
        .from('analyses')
        .select('*, profiles(id, email, role)')
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) {
        console.error('[ADMIN] Error loading analyses:', error)
        alert(`Failed to load analyses: ${error.message}`)
        setAnalyses([])
      } else {
        console.log(`[ADMIN] Loaded ${data?.length || 0} analyses`)
        setAnalyses(data || [])
      }
    } catch (error) {
      console.error('[ADMIN] Unexpected error loading analyses:', error)
      setAnalyses([])
    }
  }

  async function updateUserRole(userId, newRole, previousRole) {
    const { error } = await supabase
      .from('profiles')
      .update({
        role: newRole,
        is_premium: newRole === 'premium' || newRole === 'admin',
        queries_limit: (newRole === 'premium' || newRole === 'admin') ? 50 : (newRole === 'revoked' ? 0 : 5),
        premium_granted_at: (newRole === 'premium' || newRole === 'admin') ? new Date().toISOString() : null,
        access_revoked_at: newRole === 'revoked' ? new Date().toISOString() : null,
        access_revoked_by: newRole === 'revoked' ? user.id : null,
        last_status_change: new Date().toISOString()
      })
      .eq('id', userId)

    if (error) {
      console.error('Error updating role:', error)
      alert('Error updating user role')
    } else {
      await supabase
        .from('access_audit_log')
        .insert({
          user_id: userId,
          action: newRole === 'revoked' ? 'access_revoked' : (previousRole === 'revoked' ? 'access_restored' : 'role_changed'),
          previous_role: previousRole,
          new_role: newRole,
          changed_by: user.id,
          reason: newRole === 'revoked' ? 'Access revoked by admin' : (previousRole === 'revoked' ? 'Access restored' : 'Role updated by admin')
        })

      await loadUsers()
    }
  }

  async function revokeUserAccess(userId, userEmail) {
    if (!confirm(`Revoke access for ${userEmail}? This will prevent them from using the application.`)) {
      return
    }

    const targetUser = users.find(u => u.id === userId)
    if (!targetUser) return

    await updateUserRole(userId, 'revoked', targetUser.role)
  }

  async function restoreUserAccess(userId, userEmail) {
    if (!confirm(`Restore access for ${userEmail}?`)) {
      return
    }

    await updateUserRole(userId, 'user', 'revoked')
  }

  async function resetQueryCount(userId) {
    const { error } = await supabase
      .from('profiles')
      .update({ queries_used: 0 })
      .eq('id', userId)

    if (error) {
      console.error('Error resetting queries:', error)
    } else {
      await loadUsers()
    }
  }

  async function loadAccessRequests() {
    try {
      console.log('[ADMIN] Loading access requests...')
      const { data, error } = await supabase
        .from('access_requests')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('[ADMIN] Error loading access requests:', error)
        alert(`Failed to load access requests: ${error.message}`)
        setAccessRequests([])
        setPendingCount(0)
      } else {
        console.log(`[ADMIN] Loaded ${data?.length || 0} access requests`)
        setAccessRequests(data || [])
        setPendingCount(data?.filter(r => r.status === 'pending').length || 0)
      }
    } catch (error) {
      console.error('[ADMIN] Unexpected error loading access requests:', error)
      setAccessRequests([])
      setPendingCount(0)
    }
  }

  async function prepareChartData() {
    const { data: allAnalyses } = await supabase
      .from('analyses')
      .select('*, profiles(email, role)')
      .order('created_at', { ascending: true })

    if (!allAnalyses) return

    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - (29 - i))
      return date.toISOString().split('T')[0]
    })

    const dailyData = last30Days.map(date => {
      const count = allAnalyses.filter(a => a.created_at.startsWith(date)).length
      return { date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), count }
    })

    const userSearchCounts = {}
    allAnalyses.forEach(a => {
      const email = a.profiles?.email || 'Unknown'
      userSearchCounts[email] = (userSearchCounts[email] || 0) + 1
    })

    const searchesByUser = Object.entries(userSearchCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([email, count]) => ({
        email: email.length > 20 ? email.substring(0, 20) + '...' : email,
        searches: count
      }))

    const roleData = users.reduce((acc, u) => {
      acc[u.role] = (acc[u.role] || 0) + 1
      return acc
    }, {})

    const roleDistribution = Object.entries(roleData).map(([role, count]) => ({
      role: role.charAt(0).toUpperCase() + role.slice(1),
      count
    }))

    setChartData({ daily: dailyData, searchesByUser, roleDistribution })
  }

  function getFilteredAnalyses() {
    return analyses.filter(a => {
      if (dateFilter.start && new Date(a.created_at) < new Date(dateFilter.start)) return false
      if (dateFilter.end && new Date(a.created_at) > new Date(dateFilter.end)) return false
      if (roleFilter !== 'all' && a.profiles?.role !== roleFilter) return false
      if (confidenceFilter === 'high' && a.confidence_score < 4) return false
      if (confidenceFilter === 'medium' && (a.confidence_score < 3 || a.confidence_score >= 4)) return false
      if (confidenceFilter === 'low' && a.confidence_score >= 3) return false
      if (searchText && !a.job_text.toLowerCase().includes(searchText.toLowerCase())) return false
      return true
    })
  }

  function clearFilters() {
    setDateFilter({ start: '', end: '' })
    setRoleFilter('all')
    setConfidenceFilter('all')
    setSearchText('')
  }

  function exportToCSV(data, filename) {
    if (!data || data.length === 0) {
      alert('No data to export')
      return
    }

    const headers = Object.keys(data[0])
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header]
        if (value === null || value === undefined) return ''
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
        return `"${stringValue.replace(/"/g, '""')}"`
      }).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  useEffect(() => {
    if (users.length > 0 && analyses.length > 0) {
      prepareChartData()
    }
  }, [users, analyses])

  async function approveRequest(request) {
    if (!confirm(`Approve access request for ${request.email}?`)) {
      return
    }

    try {
      console.log('[ADMIN] Approving request for:', request.email)
      const randomPassword = Math.random().toString(36).slice(-12) + 'A1!'
      let userId = null

      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: request.email,
        password: randomPassword,
        email_confirm: true
      })

      if (authError) {
        if (authError.message.includes('already registered')) {
          console.log('[ADMIN] User already exists, will update existing profile')
          const { data: existingUser } = await supabase.auth.admin.listUsers()
          const foundUser = existingUser?.users?.find(u => u.email === request.email)
          if (foundUser) {
            userId = foundUser.id
            console.log('[ADMIN] Found existing user:', userId)
          } else {
            throw new Error('User already exists but could not be found')
          }
        } else {
          throw authError
        }
      } else {
        userId = authData.user.id
        console.log('[ADMIN] Created new user:', userId)
      }

      if (userId) {
        console.log('[ADMIN] Setting profile for user:', userId)
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: userId,
            role: 'user',
            queries_used: 0,
            queries_limit: 5,
            is_premium: false,
            email: request.email,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            last_status_change: new Date().toISOString()
          }, {
            onConflict: 'id'
          })

        if (profileError) {
          console.error('[ADMIN] Error creating/updating profile:', profileError)
        } else {
          console.log('[ADMIN] Profile created/updated successfully')

          await supabase
            .from('access_audit_log')
            .insert({
              user_id: userId,
              action: 'access_granted',
              previous_role: null,
              new_role: 'user',
              changed_by: user.id,
              reason: 'Access request approved'
            })
        }
      }

      const { error: updateError } = await supabase
        .from('access_requests')
        .update({
          status: 'approved',
          processed_at: new Date().toISOString(),
          processed_by: user.id
        })
        .eq('id', request.id)

      if (updateError) {
        throw updateError
      }

      console.log('[ADMIN] Request approved successfully')
      setTempPassword(randomPassword)
      setShowPasswordModal(true)
      await loadAccessRequests()
      await loadUsers()
    } catch (error) {
      console.error('[ADMIN] Error approving request:', error)
      alert(`Error approving request: ${error.message}`)
    }
  }

  async function rejectRequest(request) {
    const reason = prompt(`Reject access request for ${request.email}?\nOptional: Enter rejection reason:`)

    if (reason === null) {
      return
    }

    try {
      const { error } = await supabase
        .from('access_requests')
        .update({
          status: 'rejected',
          processed_at: new Date().toISOString(),
          processed_by: user.id
        })
        .eq('id', request.id)

      if (error) {
        throw error
      }

      await loadAccessRequests()
      alert(`Request from ${request.email} has been rejected.`)
    } catch (error) {
      console.error('Error rejecting request:', error)
      alert(`Error rejecting request: ${error.message}`)
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/')
  }

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <div className="nav">
        <div className="nav-content">
          <div
            className="nav-title"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <span>&gt; AgencySniper</span>
          </div>
          <div className="nav-actions">
            <button onClick={() => navigate('/dashboard')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Dashboard
            </button>
            <button onClick={() => navigate('/saved')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Saved
            </button>
            <button onClick={() => navigate('/agencies')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Agencies
            </button>
            <button onClick={handleSignOut} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{ fontSize: '32px', marginBottom: '8px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
                &gt; System Control
              </h2>
              <p style={{ color: 'rgba(152, 251, 152, 0.85)', marginBottom: '0', fontSize: '15px', textShadow: '0 0 5px rgba(152, 251, 152, 0.4)' }}>
                User management, permissions, and operation analytics
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{
                padding: '8px 16px',
                background: dbStatus.connected ? 'rgba(34, 139, 34, 0.3)' : 'rgba(139, 34, 34, 0.3)',
                border: `2px solid ${dbStatus.connected ? '#98FB98' : '#FF6B6B'}`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: dbStatus.connected ? '#98FB98' : '#FF6B6B',
                  boxShadow: dbStatus.connected ? '0 0 8px #98FB98' : '0 0 8px #FF6B6B'
                }}></div>
                <span style={{ fontSize: '13px', color: dbStatus.connected ? '#98FB98' : '#FF6B6B', fontWeight: '600' }}>
                  {dbStatus.connected ? 'DB Connected' : 'DB Error'}
                </span>
              </div>
              <button
                onClick={refreshAllData}
                disabled={dataRefreshing}
                className="btn-secondary"
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                {dataRefreshing ? (
                  <span>
                    <span className="spinner" style={{ width: '12px', height: '12px', borderWidth: '2px', display: 'inline-block', marginRight: '6px', verticalAlign: 'middle' }}></span>
                    Refreshing...
                  </span>
                ) : '↻ Refresh Data'}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab('users')}
              className={activeTab === 'users' ? 'btn' : 'btn-secondary'}
              style={{ padding: '10px 20px', fontSize: '14px' }}
            >
              User Management
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={activeTab === 'analytics' ? 'btn' : 'btn-secondary'}
              style={{ padding: '10px 20px', fontSize: '14px' }}
            >
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={activeTab === 'logs' ? 'btn' : 'btn-secondary'}
              style={{ padding: '10px 20px', fontSize: '14px' }}>
              Query Logs
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={activeTab === 'requests' ? 'btn' : 'btn-secondary'}
              style={{ padding: '10px 20px', fontSize: '14px', position: 'relative' }}
            >
              Access Requests
              {pendingCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#FF6B6B',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '700',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>
                  {pendingCount}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'users' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontSize: '24px', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)', margin: 0 }}>
                  User Management ({users.length})
                </h3>
                <button
                  onClick={() => exportToCSV(users.map(u => ({
                    email: u.email || u.id,
                    role: u.role,
                    queries_used: u.queries_used,
                    queries_limit: u.queries_limit,
                    is_premium: u.is_premium,
                    created_at: new Date(u.created_at).toLocaleString(),
                    premium_granted_at: u.premium_granted_at ? new Date(u.premium_granted_at).toLocaleString() : 'N/A'
                  })), 'users-export')}
                  className="btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                >
                  Export CSV
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Queries</th>
                      <th>Premium</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td style={{ color: '#98FB98', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: '600' }}>
                          {u.email}
                        </td>
                        <td>
                          <select
                            value={u.role}
                            onChange={(e) => updateUserRole(u.id, e.target.value, u.role)}
                            style={{
                              background: u.role === 'revoked' ? 'rgba(139, 34, 34, 0.3)' : 'rgba(34, 139, 34, 0.2)',
                              border: `2px solid ${u.role === 'revoked' ? '#FF6B6B' : 'rgba(152, 251, 152, 0.4)'}`,
                              color: u.role === 'revoked' ? '#FF6B6B' : '#98FB98',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="user">User</option>
                            <option value="premium">Premium</option>
                            <option value="admin">Admin</option>
                            <option value="revoked">Revoked</option>
                          </select>
                        </td>
                        <td style={{ color: 'rgba(152, 251, 152, 0.85)' }}>
                          {u.queries_used}/{u.queries_limit}
                        </td>
                        <td>
                          {u.is_premium ? (
                            <span className="badge badge-high">Yes</span>
                          ) : (
                            <span className="badge badge-low">No</span>
                          )}
                        </td>
                        <td style={{ color: 'rgba(152, 251, 152, 0.75)', fontSize: '13px' }}>
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {u.role === 'revoked' ? (
                              <button
                                onClick={() => restoreUserAccess(u.id, u.email)}
                                className="btn"
                                style={{ padding: '6px 12px', fontSize: '12px', background: 'rgba(34, 139, 34, 0.8)', borderColor: '#98FB98' }}
                              >
                                Restore Access
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => resetQueryCount(u.id)}
                                  className="btn-secondary"
                                  style={{ padding: '6px 12px', fontSize: '12px' }}
                                >
                                  Reset Queries
                                </button>
                                {u.role !== 'admin' && (
                                  <button
                                    onClick={() => revokeUserAccess(u.id, u.email)}
                                    className="btn-secondary"
                                    style={{ padding: '6px 12px', fontSize: '12px', background: 'rgba(139, 34, 34, 0.6)', borderColor: '#FF6B6B', color: '#FF6B6B' }}
                                  >
                                    Revoke
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {users.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(152, 251, 152, 0.65)' }}>
                  <p style={{ fontSize: '16px', marginBottom: '12px' }}>No users found</p>
                  <p style={{ fontSize: '14px' }}>This could indicate a database connection issue or RLS policy problem.</p>
                  <button
                    onClick={refreshAllData}
                    className="btn"
                    style={{ marginTop: '16px', padding: '10px 24px' }}
                  >
                    Try Refreshing
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '24px', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)', margin: 0 }}>
                  System Analytics
                </h3>
                <button
                  onClick={() => exportToCSV(analyses.map(a => ({
                    date: new Date(a.created_at).toLocaleString(),
                    user: a.profiles?.email || 'N/A',
                    job_text_snippet: a.job_text.substring(0, 100),
                    top_match: a.result_json?.companies?.[0]?.name || 'N/A',
                    confidence: a.confidence_score
                  })), 'analytics-export')}
                  className="btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                >
                  Export CSV
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '32px' }}>
                <div style={{ padding: '20px', background: 'rgba(34, 139, 34, 0.2)', borderRadius: '12px', border: '2px solid rgba(152, 251, 152, 0.3)' }}>
                  <div style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.75)', marginBottom: '8px' }}>
                    Total Users
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
                    {users.length}
                  </div>
                </div>
                <div style={{ padding: '20px', background: 'rgba(34, 139, 34, 0.2)', borderRadius: '12px', border: '2px solid rgba(152, 251, 152, 0.3)' }}>
                  <div style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.75)', marginBottom: '8px' }}>
                    Total Analyses
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
                    {analyses.length}
                  </div>
                </div>
                <div style={{ padding: '20px', background: 'rgba(34, 139, 34, 0.2)', borderRadius: '12px', border: '2px solid rgba(152, 251, 152, 0.3)' }}>
                  <div style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.75)', marginBottom: '8px' }}>
                    Premium Users
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
                    {users.filter(u => u.is_premium).length}
                  </div>
                </div>
                <div style={{ padding: '20px', background: 'rgba(34, 139, 34, 0.2)', borderRadius: '12px', border: '2px solid rgba(152, 251, 152, 0.3)' }}>
                  <div style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.75)', marginBottom: '8px' }}>
                    Avg Confidence
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
                    {analyses.length > 0
                      ? (analyses.reduce((sum, a) => sum + (a.confidence_score || 0), 0) / analyses.length).toFixed(1)
                      : '0'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                <div style={{ padding: '20px', background: 'rgba(34, 139, 34, 0.15)', borderRadius: '12px', border: '2px solid rgba(152, 251, 152, 0.3)' }}>
                  <h4 style={{ fontSize: '16px', marginBottom: '16px', color: '#98FB98', textShadow: '0 0 10px rgba(152, 251, 152, 0.6)' }}>
                    Daily Analysis Volume (Last 30 Days)
                  </h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={chartData.daily}>
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#98FB98" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#98FB98" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(152, 251, 152, 0.2)" />
                      <XAxis dataKey="date" stroke="#98FB98" style={{ fontSize: '11px' }} />
                      <YAxis stroke="#98FB98" style={{ fontSize: '11px' }} />
                      <Tooltip
                        contentStyle={{ background: 'rgba(34, 139, 34, 0.95)', border: '1px solid #98FB98', borderRadius: '8px', color: '#98FB98' }}
                      />
                      <Area type="monotone" dataKey="count" stroke="#98FB98" strokeWidth={2} fill="url(#colorCount)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ padding: '20px', background: 'rgba(34, 139, 34, 0.15)', borderRadius: '12px', border: '2px solid rgba(152, 251, 152, 0.3)' }}>
                  <h4 style={{ fontSize: '16px', marginBottom: '16px', color: '#98FB98', textShadow: '0 0 10px rgba(152, 251, 152, 0.6)' }}>
                    Top 10 Users by Searches
                  </h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData.searchesByUser} layout="horizontal">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(152, 251, 152, 0.2)" />
                      <XAxis type="number" stroke="#98FB98" style={{ fontSize: '11px' }} />
                      <YAxis type="category" dataKey="email" stroke="#98FB98" style={{ fontSize: '10px' }} width={120} />
                      <Tooltip
                        contentStyle={{ background: 'rgba(34, 139, 34, 0.95)', border: '1px solid #98FB98', borderRadius: '8px', color: '#98FB98' }}
                      />
                      <Bar dataKey="searches" fill="#98FB98" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontSize: '24px', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)', margin: 0 }}>
                  Query Logs ({getFilteredAnalyses().length})
                </h3>
                <button
                  onClick={() => exportToCSV(getFilteredAnalyses().map(a => ({
                    date: new Date(a.created_at).toLocaleString(),
                    user_email: a.profiles?.email || 'N/A',
                    user_role: a.profiles?.role || 'N/A',
                    job_text: a.job_text,
                    top_match: a.result_json?.companies?.[0]?.name || 'N/A',
                    confidence_score: a.confidence_score
                  })), 'query-logs')}
                  className="btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                >
                  Export Filtered CSV
                </button>
              </div>

              <div style={{ marginBottom: '24px', padding: '20px', background: 'rgba(34, 139, 34, 0.15)', borderRadius: '12px', border: '2px solid rgba(152, 251, 152, 0.3)' }}>
                <h4 style={{ fontSize: '16px', marginBottom: '16px', color: '#98FB98', textShadow: '0 0 10px rgba(152, 251, 152, 0.6)' }}>
                  Filters
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label className="label" style={{ fontSize: '13px' }}>Start Date</label>
                    <input
                      type="date"
                      className="input"
                      value={dateFilter.start}
                      onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
                      style={{ padding: '8px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label className="label" style={{ fontSize: '13px' }}>End Date</label>
                    <input
                      type="date"
                      className="input"
                      value={dateFilter.end}
                      onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
                      style={{ padding: '8px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label className="label" style={{ fontSize: '13px' }}>User Role</label>
                    <select
                      className="input"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      style={{ padding: '8px', fontSize: '13px' }}
                    >
                      <option value="all">All Roles</option>
                      <option value="user">User</option>
                      <option value="premium">Premium</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="label" style={{ fontSize: '13px' }}>Confidence Score</label>
                    <select
                      className="input"
                      value={confidenceFilter}
                      onChange={(e) => setConfidenceFilter(e.target.value)}
                      style={{ padding: '8px', fontSize: '13px' }}
                    >
                      <option value="all">All Scores</option>
                      <option value="high">High (4-5)</option>
                      <option value="medium">Medium (3)</option>
                      <option value="low">Low (1-2)</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label className="label" style={{ fontSize: '13px' }}>Search Job Text</label>
                  <input
                    type="text"
                    className="input"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search within job descriptions..."
                    style={{ padding: '8px', fontSize: '13px' }}
                  />
                </div>
                <button
                  onClick={clearFilters}
                  className="btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                >
                  Clear All Filters
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>Role</th>
                      <th>Job Text</th>
                      <th>Top Match</th>
                      <th>Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredAnalyses().map((analysis) => {
                      const topCompany = analysis.result_json?.companies?.[0]?.name || 'N/A'
                      return (
                        <tr key={analysis.id}>
                          <td style={{ color: 'rgba(152, 251, 152, 0.75)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                            {new Date(analysis.created_at).toLocaleString()}
                          </td>
                          <td style={{ color: 'rgba(152, 251, 152, 0.85)', fontSize: '13px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {analysis.profiles?.email || 'N/A'}
                          </td>
                          <td>
                            <span className={`badge ${
                              analysis.profiles?.role === 'admin' ? 'badge-high' :
                              analysis.profiles?.role === 'premium' ? 'badge-medium' :
                              'badge-low'
                            }`} style={{ fontSize: '11px', padding: '4px 8px' }}>
                              {analysis.profiles?.role || 'N/A'}
                            </span>
                          </td>
                          <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', color: 'rgba(152, 251, 152, 0.75)' }}>
                            {analysis.job_text.substring(0, 80)}...
                          </td>
                          <td style={{ color: '#98FB98', fontWeight: '700' }}>
                            {topCompany}
                          </td>
                          <td>
                            <span className={`badge ${analysis.confidence_score >= 4 ? 'badge-high' : analysis.confidence_score >= 3 ? 'badge-medium' : 'badge-low'}`}>
                              {analysis.confidence_score}/5
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {getFilteredAnalyses().length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(152, 251, 152, 0.65)' }}>
                  <p style={{ fontSize: '16px' }}>No logs match the current filters</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'requests' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontSize: '24px', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)', margin: 0 }}>
                  Access Requests ({accessRequests.filter(r => requestFilter === 'all' || r.status === requestFilter).length})
                </h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    onClick={() => exportToCSV(accessRequests.map(r => ({
                      email: r.email,
                      reason: r.reason,
                      status: r.status,
                      created_at: new Date(r.created_at).toLocaleString(),
                      processed_at: r.processed_at ? new Date(r.processed_at).toLocaleString() : 'N/A',
                      processed_by: r.processed_by || 'N/A'
                    })), 'access-requests')}
                    className="btn-secondary"
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    Export CSV
                  </button>
                  <button
                    onClick={() => setRequestFilter('all')}
                    className={requestFilter === 'all' ? 'btn' : 'btn-secondary'}
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setRequestFilter('pending')}
                    className={requestFilter === 'pending' ? 'btn' : 'btn-secondary'}
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    Pending {pendingCount > 0 && `(${pendingCount})`}
                  </button>
                  <button
                    onClick={() => setRequestFilter('approved')}
                    className={requestFilter === 'approved' ? 'btn' : 'btn-secondary'}
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    Approved
                  </button>
                  <button
                    onClick={() => setRequestFilter('rejected')}
                    className={requestFilter === 'rejected' ? 'btn' : 'btn-secondary'}
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    Rejected
                  </button>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Reason</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessRequests
                      .filter(r => requestFilter === 'all' || r.status === requestFilter)
                      .map((request) => (
                        <tr key={request.id}>
                          <td style={{ color: '#98FB98', fontWeight: '600', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {request.email}
                          </td>
                          <td style={{ color: 'rgba(152, 251, 152, 0.85)', maxWidth: '300px' }}>
                            {request.reason.length > 100 ? (
                              <details>
                                <summary style={{ cursor: 'pointer', color: '#98FB98' }}>
                                  {request.reason.substring(0, 100)}...
                                </summary>
                                <p style={{ marginTop: '8px', lineHeight: '1.6' }}>{request.reason}</p>
                              </details>
                            ) : (
                              request.reason
                            )}
                          </td>
                          <td style={{ color: 'rgba(152, 251, 152, 0.75)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                            {new Date(request.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <span className={`badge ${
                              request.status === 'approved' ? 'badge-high' :
                              request.status === 'pending' ? 'badge-medium' :
                              'badge-low'
                            }`}>
                              {request.status}
                            </span>
                          </td>
                          <td>
                            {request.status === 'pending' && (
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => approveRequest(request)}
                                  className="btn"
                                  style={{ padding: '6px 12px', fontSize: '12px' }}
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => rejectRequest(request)}
                                  className="btn-secondary"
                                  style={{ padding: '6px 12px', fontSize: '12px' }}
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                            {request.status !== 'pending' && (
                              <span style={{ fontSize: '12px', color: 'rgba(152, 251, 152, 0.65)' }}>
                                {request.processed_at ? new Date(request.processed_at).toLocaleDateString() : '-'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {accessRequests.filter(r => requestFilter === 'all' || r.status === requestFilter).length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(152, 251, 152, 0.65)' }}>
                  <p style={{ fontSize: '16px' }}>No {requestFilter !== 'all' ? requestFilter : ''} requests found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '100%', margin: 0 }}>
            <h3 style={{ fontSize: '24px', marginBottom: '20px', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
              &gt; User Approved Successfully
            </h3>
            <p style={{ color: 'rgba(152, 251, 152, 0.9)', marginBottom: '16px', lineHeight: '1.6' }}>
              The user account has been created. Please share this temporary password with the user:
            </p>
            <div style={{
              padding: '16px',
              background: 'rgba(34, 139, 34, 0.2)',
              borderRadius: '8px',
              border: '2px solid rgba(152, 251, 152, 0.4)',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '13px', color: 'rgba(152, 251, 152, 0.75)', marginBottom: '8px' }}>Temporary Password:</div>
              <div style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#98FB98',
                fontFamily: 'Space Mono, Cousine, monospace',
                letterSpacing: '2px',
                textShadow: '0 0 10px rgba(152, 251, 152, 0.7)',
                userSelect: 'all'
              }}>
                {tempPassword}
              </div>
            </div>
            <p style={{ color: 'rgba(152, 251, 152, 0.8)', fontSize: '13px', marginBottom: '20px' }}>
              Note: The user should change this password after their first login.
            </p>
            <button
              onClick={() => {
                setShowPasswordModal(false)
                setTempPassword('')
              }}
              className="btn"
              style={{ width: '100%' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
